import Router from 'next/router';
import { useMemo } from 'react';

import { type BreadcrumbsItemType } from '@air/component-breadcrumbs';

import { AncestorImageContainer } from '~/components/AncestorImageContainer';
import { createLibraryRoute } from '~/constants/routes';
import { type CentralizedClipBoardInfo } from '~/store/centralizedClip/selectors';
import { type LocationBoard } from '~/store/router/types';

export type UseAssetLocationBreadcrumbsOptions = {
  onBoardClick: (board: LocationBoard) => void;
  excludeLibrary: boolean;
};

type UseAssetLocationBreadcrumbItemsOptions = UseAssetLocationBreadcrumbsOptions & {
  current: CentralizedClipBoardInfo['current'];
};

/**
 * The asset's LOCATION crumbs — library (private only), ancestor boards, current board — and
 * nothing else: the title item is the caller's to append (useAssetTitleBreadcrumbItem), so each
 * breadcrumbs component composes its own full trail explicitly.
 */
export const useAssetLocationBreadcrumbItems = ({
  current,
  onBoardClick,
  excludeLibrary,
}: UseAssetLocationBreadcrumbItemsOptions) => {
  const assetLocationBreadcrumbItems = useMemo(() => {
    const items: BreadcrumbsItemType[] = [];

    if (current?.library && !excludeLibrary) {
      items.push({
        className: 'text-inherit',
        id: current.library.id,
        prefix: (
          <AncestorImageContainer backgroundColor={current.library.color.backgroundHex}>
            <p className="text-14" data-testid="library-icon">
              {current.library.icon}
            </p>
          </AncestorImageContainer>
        ),
        label: current.library.title,
        type: 'button',
        onClick: () => {
          if (current?.library?.id) {
            void Router.push(createLibraryRoute(current.library.id));
          }
        },
      });
    }

    if (current?.ancestors) {
      current.ancestors.forEach((ancestor) => {
        items.push({
          className: 'text-inherit',
          id: ancestor.id,
          label: ancestor.title,
          type: 'button',
          onClick: () => {
            onBoardClick(ancestor);
          },
        });
      });
    }

    if (current) {
      items.push({
        className: 'text-inherit',
        id: current.id,
        label: current.title,
        type: 'button',
        onClick: () => {
          onBoardClick(current);
        },
      });
    }

    return items;
  }, [current, onBoardClick, excludeLibrary]);

  return { assetLocationBreadcrumbItems };
};
